import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Flag, MoreHorizontal, ExternalLink, X, ChevronDown, ChevronUp, Flame, Zap, Pin, Share2 } from 'lucide-react';
import { WallPost } from '@/hooks/useSocialWall';
import { cn } from '@/lib/utils';
import PostComments from './PostComments';
import LinkPreviewCard from './LinkPreviewCard';
import StatsCard from './StatsCard';
import TemplatePostDisplay from './TemplatePostDisplay';
import ReactionPicker from './ReactionPicker';
import ReactionDisplay from './ReactionDisplay';
import HashtagText from './HashtagText';
import BadgeDisplay from './BadgeDisplay';
import BoostPostButton from './BoostPostButton';
import TrainingWallPostCard from './TrainingWallPostCard';
import { useUserBadges } from '@/hooks/useUserBadges';
import SharePostModal from './SharePostModal';

interface AllowanceInfo {
  remaining: number;
  weeklyLimit: number;
}

interface WallPostCardProps {
  post: WallPost;
  onReaction: (postId: string, reactionType: string, hasReaction: boolean) => void;
  onReport: (postId: string, reason: string, details?: string) => void;
  onPinPost?: (postId: string) => Promise<boolean>;
  onUnpinPost?: (postId: string) => Promise<boolean>;
  currentUserId?: string;
  currentUserAvatar?: string | null;
  currentUserName?: string | null;
  reactionAllowances?: Record<string, AllowanceInfo>;
  tokenBalance?: number;
  onBoostPost?: (postId: string) => Promise<boolean>;
}

const WallPostCard: React.FC<WallPostCardProps> = ({
  post,
  onReaction,
  onReport,
  onPinPost,
  onUnpinPost,
  currentUserId,
  currentUserAvatar,
  currentUserName,
  reactionAllowances,
  tokenBalance = 0,
  onBoostPost,
}) => {
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [pinConfirmOpen, setPinConfirmOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Fetch badges for the post author
  const { badges: authorBadges } = useUserBadges(post.profile_id);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleQuickReaction = () => {
    // Quick tap defaults to fire
    const hasFireReaction = post.user_reactions?.includes('fire');
    onReaction(post.id, 'fire', hasFireReaction);
  };

  const handleReactionSelect = (reactionType: string) => {
    const hasReaction = post.user_reactions?.includes(reactionType);
    onReaction(post.id, reactionType, hasReaction);
    setShowReactionPicker(false);
  };

  const handleReactionClick = (reactionType: string) => {
    const hasReaction = post.user_reactions?.includes(reactionType);
    onReaction(post.id, reactionType, hasReaction);
  };

  const handleReport = () => {
    if (!reportReason.trim()) return;
    onReport(post.id, reportReason, reportDetails);
    setReportDialogOpen(false);
    setReportReason('');
    setReportDetails('');
  };

  const openLightbox = (url: string) => {
    setLightboxImage(url);
    setLightboxOpen(true);
  };

  const handlePinPost = async () => {
    if (onPinPost) {
      await onPinPost(post.id);
    }
    setPinConfirmOpen(false);
  };

  const handleUnpinPost = async () => {
    if (onUnpinPost) {
      await onUnpinPost(post.id);
    }
  };

  const isOwnPost = currentUserId === post.profile_id;
  const hasAnyReaction = (post.user_reactions?.length || 0) > 0;
  const totalReactions = Object.values(post.reactions_count || {}).reduce((a, b) => a + b, 0);

  // Separate images and videos
  const mediaItems = post.media_urls || [];
  const images = mediaItems.filter(url => !url.match(/\.(mp4|mov|webm|ogg)$/i));
  const videos = mediaItems.filter(url => url.match(/\.(mp4|mov|webm|ogg)$/i));

  const getMediaGridClass = (count: number) => {
    if (count === 1) return 'grid-cols-1';
    if (count === 2) return 'grid-cols-2';
    return 'grid-cols-2';
  };

  return (
    <>
      <Card className={cn(
        "mb-4 transition-all relative",
        post.is_pinned && "ring-2 ring-primary/40 bg-gradient-to-br from-primary/5 to-transparent",
        post.is_trending && "ring-2 ring-amber-400/60 shadow-[0_0_15px_rgba(251,191,36,0.3)]",
        post.is_boosted && post.boosted_until && new Date(post.boosted_until) > new Date() && 
          "ring-2 ring-amber-500/40 bg-gradient-to-br from-amber-50/50 to-transparent dark:from-amber-950/20"
      )}>
        {/* Pinned Badge */}
        {post.is_pinned && (
          <div className="absolute -top-2 left-4 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 z-10">
            <Pin className="h-3 w-3" />
            Pinned
          </div>
        )}
        {/* Trending Badge */}
        {post.is_trending && !post.is_pinned && (
          <div className="absolute -top-2 left-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 z-10">
            <Flame className="h-3 w-3" />
            Trending
          </div>
        )}
        {/* Boosted Badge */}
        {post.is_boosted && post.boosted_until && new Date(post.boosted_until) > new Date() && !post.is_trending && !post.is_pinned && (
          <div className="absolute -top-2 left-4 bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 z-10">
            <Zap className="h-3 w-3 fill-current" />
            Boosted
          </div>
        )}
        <CardContent className={cn("pt-4", (post.is_trending || post.is_pinned) && "pt-6")}>
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <Link to={`/profile/${post.profiles?.username || post.profile_id}`}>
                <Avatar className="h-10 w-10">
                  <AvatarImage
                    src={post.profiles?.avatar_url || undefined}
                    alt={post.profiles?.name || 'User'}
                  />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {getInitials(post.profiles?.name)}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <div>
                <div className="flex items-center gap-1.5">
                  <Link
                    to={`/profile/${post.profiles?.username || post.profile_id}`}
                    className="font-medium hover:underline"
                  >
                    {post.profiles?.name || 'Unknown User'}
                  </Link>
                  {authorBadges.length > 0 && (
                    <BadgeDisplay badges={authorBadges} maxDisplay={3} size="sm" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>

            {/* Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isOwnPost && (
                  <>
                    {post.is_pinned ? (
                      <DropdownMenuItem onClick={handleUnpinPost}>
                        <Pin className="h-4 w-4 mr-2" />
                        Unpin from Profile
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => setPinConfirmOpen(true)}>
                        <Pin className="h-4 w-4 mr-2" />
                        Pin to Profile
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                  </>
                )}
                {!isOwnPost && (
                  <DropdownMenuItem onClick={() => setReportDialogOpen(true)}>
                    <Flag className="h-4 w-4 mr-2" />
                    Report Post
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Training Post Display */}
          {post.post_type === 'training' && post.template_data && (
            <div className="mb-3">
              <TrainingWallPostCard
                templateData={post.template_data as any}
                profileId={post.profile_id}
                profileUsername={post.profiles?.username || undefined}
              />
            </div>
          )}

          {/* Template Post Display */}
          {post.template_id && post.template_data && post.post_type !== 'training' && (
            <div className="mb-3">
              <TemplatePostDisplay
                templateId={post.template_id}
                templateData={post.template_data as Record<string, string | string[]>}
                postId={post.id}
              />
            </div>
          )}

          {/* Content (only for non-template/non-training posts) */}
          {!post.template_id && post.post_type !== 'training' && post.content && (
            <p className="mb-3 whitespace-pre-wrap">
              <HashtagText content={post.content} />
            </p>
          )}

          {/* Images Grid */}
          {images.length > 0 && (
            <div className={`grid gap-1 mb-3 rounded-lg overflow-hidden ${getMediaGridClass(images.length)}`}>
              {images.map((url, index) => (
                <div
                  key={index}
                  className={cn(
                    'relative cursor-pointer overflow-hidden',
                    images.length === 1 && 'aspect-video',
                    images.length === 2 && 'aspect-square',
                    images.length === 3 && index === 0 && 'row-span-2 aspect-auto h-full',
                    images.length === 3 && index > 0 && 'aspect-square',
                    images.length === 4 && 'aspect-square'
                  )}
                  onClick={() => openLightbox(url)}
                >
                  <img
                    src={url}
                    alt={`Post media ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Videos */}
          {videos.length > 0 && (
            <div className="mb-3 space-y-2">
              {videos.map((url, index) => (
                <video
                  key={index}
                  src={url}
                  controls
                  className="w-full max-h-[400px] rounded-lg bg-muted"
                />
              ))}
            </div>
          )}

          {/* Link Preview */}
          {post.link_url && post.link_preview && (
            <div className="mb-3">
              <LinkPreviewCard
                preview={{
                  title: post.link_preview.title || null,
                  description: post.link_preview.description || null,
                  image: post.link_preview.image || null,
                  domain: post.link_preview.domain || new URL(post.link_url).hostname.replace('www.', ''),
                  isYouTube: post.link_preview.isYouTube,
                  videoId: post.link_preview.videoId,
                }}
                url={post.link_url}
              />
            </div>
          )}

          {/* Fallback for link without preview data */}
          {post.link_url && !post.link_preview && (
            <a
              href={post.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-3 p-3 border rounded-lg hover:bg-accent transition-colors"
            >
              <div className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm truncate">{post.link_url}</span>
              </div>
            </a>
          )}

          {/* Stats Card */}
          {post.post_type === 'stats' && post.stats_data && (
            <div className="mb-3">
              <StatsCard statsData={post.stats_data as any} />
            </div>
          )}

          {/* Reaction Display */}
          <ReactionDisplay
            reactionsCount={post.reactions_count || {}}
            userReactions={post.user_reactions || []}
            onReactionClick={handleReactionClick}
          />

          {/* Actions */}
          <div className="flex items-center gap-4 pt-2 border-t mt-2">
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleQuickReaction}
                onMouseEnter={() => setShowReactionPicker(true)}
                className={cn(
                  'gap-2',
                  hasAnyReaction && 'text-orange-500 hover:text-orange-600'
                )}
              >
                <Flame
                  className={cn('h-4 w-4', hasAnyReaction && 'fill-current')}
                />
                <span>{totalReactions || 0}</span>
              </Button>
              
              {showReactionPicker && (
                <ReactionPicker
                  onSelect={handleReactionSelect}
                  onClose={() => setShowReactionPicker(false)}
                  allowances={reactionAllowances}
                />
              )}
            </div>

            <Button 
              variant="ghost" 
              size="sm" 
              className="gap-2"
              onClick={() => setCommentsExpanded(!commentsExpanded)}
            >
              <MessageCircle className="h-4 w-4" />
              <span>{post.comments_count || 0}</span>
              {commentsExpanded ? (
                <ChevronUp className="h-3 w-3 ml-1" />
              ) : (
                <ChevronDown className="h-3 w-3 ml-1" />
              )}
            </Button>

            {/* Share Button */}
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={() => setShareModalOpen(true)}
            >
              <Share2 className="h-4 w-4" />
              {(post.share_count || 0) > 0 && <span>{post.share_count}</span>}
            </Button>

            {/* Boost Button - only on own posts */}
            {isOwnPost && onBoostPost && (
              <BoostPostButton
                postId={post.id}
                profileId={currentUserId || ''}
                isOwnPost={isOwnPost}
                isBoosted={post.is_boosted || false}
                boostedUntil={post.boosted_until}
                tokenBalance={tokenBalance}
                onBoost={onBoostPost}
              />
            )}
          </div>

          {/* Comments Section */}
          {commentsExpanded && (
            <PostComments
              postId={post.id}
              postAuthorId={post.profiles?.id || post.profile_id}
              currentUserId={currentUserId}
              currentUserAvatar={currentUserAvatar}
              currentUserName={currentUserName}
            />
          )}
        </CardContent>
      </Card>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Post</DialogTitle>
            <DialogDescription>
              Why are you reporting this post? This will be reviewed by our team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason</label>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Select a reason...</option>
                <option value="spam">Spam</option>
                <option value="harassment">Harassment or bullying</option>
                <option value="inappropriate">Inappropriate content</option>
                <option value="misinformation">False information</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Additional details (optional)</label>
              <Textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                placeholder="Provide more context..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReport} disabled={!reportReason}>
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox for images */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black/90">
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 z-10"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          {lightboxImage && (
            <img
              src={lightboxImage}
              alt="Full size"
              className="w-full h-auto max-h-[90vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Pin Confirmation Dialog */}
      <AlertDialog open={pinConfirmOpen} onOpenChange={setPinConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pin this post?</AlertDialogTitle>
            <AlertDialogDescription>
              This post will appear at the top of your profile. If you have another pinned post, it will be unpinned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePinPost}>
              <Pin className="h-4 w-4 mr-2" />
              Pin Post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Modal */}
      <SharePostModal
        post={post}
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
      />
    </>
  );
};

export default WallPostCard;
